import { Post } from "@/src/models/post.model";
import { isNewsPost } from "@/src/utils/news-post.utils";
import { useTheme } from "@/src/styles/theme-provider";
import { useRouter } from "expo-router";
import { Alarm } from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";
import { Pressable, Text, View } from "react-native";

type NewsPostBannerProps = Readonly<{
  post: Pick<Post, "newsId" | "newsTitle">;
}>;

/**
 * The strip that marks a feed post as a mirrored news article.
 *
 * The post below it is an ordinary post in every other respect — this is the
 * only thing that distinguishes it, and it doubles as the way back to the full
 * article, which the post body only carries an excerpt of.
 *
 * Renders nothing for a post that is not a mirror, so callers can drop it in
 * unconditionally (same shape as the life-update strip it sits beside).
 */
const NewsPostBanner = ({ post }: NewsPostBannerProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { themeVars } = useTheme();

  if (!isNewsPost(post)) return null;

  return (
    <Pressable
      testID="news-post-banner"
      accessibilityRole="link"
      onPress={() =>
        router.push({
          pathname: "/(tabs)/news/news-detail",
          params: { newsId: post.newsId as string },
        })
      }
      className="flex-row items-center justify-between gap-2 px-4 py-2 bg-primary/10 border-b border-ash-3 hover:opacity-80"
    >
      <View className="flex-row items-center gap-2">
        <Alarm
          size={14}
          color={themeVars["--color-primary-default"] as string}
          variant="Bold"
        />
        <Text className="text-primary text-xs font-semibold">
          {t("LABEL_NEWS")}
        </Text>
      </View>
      <Text className="text-primary text-xs font-medium">
        {t("LABEL_READ_NEWS")}
      </Text>
    </Pressable>
  );
};

export default NewsPostBanner;
